import NavigationRoutes from "constants/NavigationRoutes";

export interface BreadcrumbRoute {
  path: string;
  label: string;
  parent?: string;
  dynamic?: boolean;
}

const {
  workforce,
  workforceDetails,
  payroll,
  profile,
  settings,
  editPayroll,
  payrollDetail,
  payment,
  timeoffLeave,
  feedback,
  holidays,
  payrollHistory,
  paystubs,
  edgeWallet,
  timeoff,
  timeoffRequest,
  peers,
  paySlipDetail,
  eprofile,
  vivo,
  edgeAtlas,
  talentNetwork,
  talentNetworkProfile,
  shortlist,
  timesheet,
} = NavigationRoutes;

export const breadcrumbRoutes: BreadcrumbRoute[] = [
  { path: "/", label: "Home" },
  { path: workforce, label: "Workforce", parent: "/" },
  { path: workforceDetails, label: "Employee", parent: "/main/workforce", dynamic: true },
  { path: payroll, label: "Billing", parent: "/" },
  { path: payrollHistory, label: "Invoices", parent: "/" },
  { path: editPayroll, label: "Adjust Pay", parent: "/main/payroll" },
  { path: payrollDetail, label: "Detail", parent: "/main/payroll", dynamic: true },
  { path: timeoffLeave.replace("#Pending", ""), label: "Time-off", parent: "/" },
  { path: holidays, label: "Holiday calendar", parent: "/" },
  { path: payment, label: "Payment method", parent: "/" },
  { path: `${payment}/add-payment-method`, label: "Add payment method", parent: payment },
  { path: settings, label: "Account settings", parent: "/" },
  { path: talentNetwork, label: "Talent Network", parent: "/" },
  { path: talentNetworkProfile, label: "Profile", parent: talentNetwork, dynamic: true },
  { path: shortlist, label: "Shortlist", parent: "/" },
  { path: profile, label: "Profile", parent: "/" },
  { path: feedback, label: "Feedback", parent: "/" },
  { path: paystubs, label: "Pay slips", parent: "/" },
  { path: paySlipDetail, label: "Pay slip", parent: "/main/paystubs", dynamic: true },
  { path: edgeWallet, label: "Edge Wallet", parent: "/" },
  { path: timeoff, label: "Time-off", parent: "/" },
  { path: timeoffRequest, label: "Request Time-off", parent: "/main/timeoff" },
  { path: peers, label: "Team", parent: "/" },
  { path: eprofile, label: "Profile", parent: "/" },
  { path: vivo, label: "Edge Vivo", parent: "/" },
  { path: edgeAtlas, label: "Edge Atlas", parent: "/" },
  { path: timesheet, label: "Timesheet", parent: "/" },
];

function isDynamicPathMatch(routePath: string, actualPath: string) {
  const routeSegments = routePath.split("/");
  const pathSegments = actualPath.split("/");

  if (routeSegments.length !== pathSegments.length) {
    return false;
  }

  for (let i = 0; i < routeSegments.length; i += 1) {
    const routeSegment = routeSegments[i];
    const pathSegment = pathSegments[i];

    if (routeSegment.startsWith(":")) {
      // eslint-disable-next-line no-continue
      continue;
    }

    if (routeSegment !== pathSegment) {
      return false;
    }
  }

  return true;
}

function findRouteByPath(path: string): BreadcrumbRoute | undefined {
  const exactMatch = breadcrumbRoutes.find((route) => route.path === path);
  if (exactMatch) {
    return exactMatch;
  }

  const dynamicMatch = breadcrumbRoutes.find((route) => route.dynamic && isDynamicPathMatch(route.path, path));

  if (dynamicMatch) {
    return {
      ...dynamicMatch,
      path,
    };
  }

  return undefined;
}

export function getBreadcrumbTrail(
  pathname: string,
  employeesInfo: any,
  dynamicLabels: Record<string, string> = {}
): BreadcrumbRoute[] {
  const trail: BreadcrumbRoute[] = [];
  const pathnameWithoutQuery = pathname.split("?")[0];
  let currentPath = pathnameWithoutQuery;
  let current = findRouteByPath(currentPath);
  const employees = Array.isArray(employeesInfo) ? employeesInfo : employeesInfo?.rows || [];

  while (current) {
    const updatedRoute = { ...current };
    const routePathWithoutQuery = updatedRoute.path.split("?")[0];

    if (dynamicLabels[routePathWithoutQuery]) {
      updatedRoute.label = dynamicLabels[routePathWithoutQuery];
    } else if (updatedRoute.path.includes(workforceDetails.replace(":id", "")) && employees.length > 0) {
      const pathParts = updatedRoute.path.split("/");
      const employeeId = pathParts[pathParts.length - 1];
      const employee = employees.find((emp) => emp.employeeId === employeeId);
      if (employee) {
        updatedRoute.label = `${employee.firstName} ${employee.lastName}`;
      }
    }
    trail.unshift(updatedRoute);
    currentPath = updatedRoute.parent || "";
    current = updatedRoute.parent ? findRouteByPath(updatedRoute.parent) : undefined;
  }

  return trail;
}
